import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../app/store";
import {
  GameDefinition,
  GameOption,
  GameState,
  Play,
  Question,
} from "./game-state.type";

const initialState: GameState = {
  options: {
    allIds: [],
    byId: {},
  },
};

export const gameStateSlice = createSlice({
  name: "game-state",
  initialState: initialState,
  reducers: {
    setGameOptions: (
      state,
      action: PayloadAction<{ id: string; options: GameOption[] }>
    ) => {
      state.gameId = action.payload.id;
      state.options.allIds = action.payload.options.map((option) => option.id);
      state.options.byId = action.payload.options.reduce((acc, current) => {
        acc[current.id] = {
          id: current.id,
          imageUrl: current.imageUrl ? current.imageUrl : `${current.id}.jpg`,
          soundUrl: current.soundUrl ? current.soundUrl : `${current.id}.mp3`,
        };
        return acc;
      }, {} as Record<string, GameOption>);
      state.definition = undefined;
      state.play = undefined;
    },
    setGameDefinition: (state, action: PayloadAction<GameDefinition>) => {
      state.definition = action.payload;
      state.play = {
        currentQuestion: 0,
        currentQuestionStatus: "not-answered",
        answers: [],
        finished: false,
      };
    },
    provideAnswer: (state, action: PayloadAction<string>) => {
      if (state.play) {
        state.play.answers.push(action.payload);
        state.play.currentQuestionStatus = "answered";
      }
    },
    next: (state) => {
      if (state.play && state.definition) {
        if (
          state.play.currentQuestion + 1 ===
          state.definition.questions.length
        ) {
          state.play.finished = true;
        } else {
          state.play.currentQuestion++;
          state.play.currentQuestionStatus = "not-answered";
        }
      }
    },
    reset: (state) => {
      state.definition = undefined;
      state.play = undefined;
    },
    resetGame: () => {
      return initialState;
    },
  },
});

export const {
  setGameOptions,
  setGameDefinition,
  provideAnswer,
  next,
  resetGame,
} = gameStateSlice.actions;

export const selectAnswerState = ({
  gameState: { definition, play },
}: RootState): AnswerState | undefined => {
  if (play && definition) {
    const correct = play.answers.filter(
      (answer, index) => answer === definition.questions[index].correctOption
    ).length;
    return {
      correct,
      incorrect: play.answers.length - correct,
      total: definition.questions.length,
    };
  }
  return undefined;
};

export interface AnswerState {
  correct: number;
  incorrect: number;
  total: number;
}

export const selectCurrentQuestion = (
  state: RootState
): Question | undefined => {
  if (state.gameState.play) {
    return state.gameState.definition?.questions[
      state.gameState.play.currentQuestion
    ];
  }
};

export const selectNextQuestion = (state: RootState): Question | undefined => {
  if (state.gameState.play) {
    return state.gameState.definition?.questions[
      state.gameState.play.currentQuestion + 1
    ];
  }
};

export const selectPlay = (state: RootState): Play | undefined => {
  return state.gameState.play;
};

export const selectOptions = (state: RootState): Record<string, GameOption> =>
  state.gameState.options.byId;

export default gameStateSlice.reducer;
